import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  CompassColor,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import { ProgressBar } from "@/components/ProgressBar";
import { FadeInAndOut } from "@/components/animation/fade";
import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { useAppContext } from "@/contexts";

import { BaseInterstitial } from "../../core";

const QUESTION_ID = "personalizingProgramV2";

type Slide = {
  content: React.ReactElement;
  durationMs?: number;
};

const SlideText: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Text
    as="p"
    fontFamily="Untitled Serif"
    fontWeight={500}
    fontSize="28px"
    lineHeight="36px"
    letterSpacing="0px"
    textAlign="center"
  >
    {children}
  </Text>
);

const PROGRESS_BAR_DURATION = window.Cypress ? 500 : 4000;
const SLIDE_DURATION_MS = window.Cypress ? 500 : 2000;

const PersonalizingProgramV2: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const { selectedProgram, setSelectedProgram } = useAppContext();
  const [currentSlideIdx, setCurrentSlideIdx] = useState(0);
  useTranslation();

  useEffect(() => {
    // if this is the first time the user is seeing this screen selectedProgram may be null
    // if they've back navigated, we'll have set selectedProgram to either DPP or WEIGHT
    // setting selectedProgram again ensure that the correct program is set in the event
    // the user back navigated and dpp eligibility changed
    if ([null, "DPP", "WEIGHT"].includes(selectedProgram)) {
      setSelectedProgram(surveyAnswers.dppEligible ? "DPP" : "WEIGHT");
    }
  }, []);

  const slides: Slide[] = [
    {
      content: (
        <Stack spacing={Spacing[16]} alignItems="center">
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.analyzing" />
          </SlideText>
          <ProgressBar
            totalTimeMs={PROGRESS_BAR_DURATION}
            borderRadius="60px"
            height="24px"
            trackColor={CompassColor.sand01}
          />
        </Stack>
      ),
      durationMs: PROGRESS_BAR_DURATION,
    },
    {
      content: (
        <FadeInAndOut duration={SLIDE_DURATION_MS / 1000}>
          <SlideText>
            <Trans
              i18nKey="personalizationProgramV2.greeting"
              values={{
                firstName: surveyAnswers.eligibilityCheck?.firstName,
              }}
            />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
    {
      content: (
        <FadeInAndOut
          duration={SLIDE_DURATION_MS / 1000}
          delay={SLIDE_DURATION_MS / 1000}
        >
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.personalizedPlan" />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
    {
      content: (
        <FadeInAndOut
          duration={SLIDE_DURATION_MS / 1000}
          delay={(SLIDE_DURATION_MS * 2) / 1000}
        >
          <SlideText>
            <Trans i18nKey="personalizationProgramV2.reachGoals" />
          </SlideText>
        </FadeInAndOut>
      ),
      durationMs: SLIDE_DURATION_MS,
    },
  ];

  useEffect(() => {
    if (currentSlideIdx < slides.length - 1) {
      const timer = setTimeout(() => {
        setCurrentSlideIdx(currentSlideIdx + 1);
      }, slides[currentSlideIdx].durationMs);
      return () => clearTimeout(timer);
    }
    setTimeout(() => {
      onClickNext(null, { updatedProgram: selectedProgram });
    }, slides[currentSlideIdx].durationMs);
  }, [currentSlideIdx]);

  return (
    <BaseInterstitial
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
      backgroundColor={CompassColor.offWhite}
      centerChildren
    >
      {slides[currentSlideIdx].content}
    </BaseInterstitial>
  );
};

export const Q_PERSONALIZING_PROGRAM_V2: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ selectedProgram, experiments }) =>
    experiments["diabetes-program-triage"] === "variant" &&
    selectedProgram !== "MED" &&
    selectedProgram !== "MOOD" &&
    selectedProgram !== "NMPBA",
  component: PersonalizingProgramV2,
};
